import type {
  AppointmentParticipant,
  Appointment as FhirAppointment,
  HealthcareService,
  Patient,
  Practitioner,
  PractitionerRole,
} from '@medplum/fhirtypes'
import { compact, invert } from 'lodash-es'
import { z } from 'zod'
import {
  codeFromCodeableConcept,
  getCodeableConceptBySystem,
} from '#src/codeableConcept.ts'
import * as codeSystems from '#src/codeSystems.ts'
import { elationSandboxIdentifier, extractElationId } from '#src/elation.ts'
import { invariant } from '#src/invariant.ts'
import { addFhirRef, metadataSchema } from '#src/models/elation/metadata.ts'
import { createReference } from '#src/references.ts'
import { build } from '#src/utils.ts'

export const AppointmentStatus = {
  SCHEDULED: 'Scheduled',
  CONFIRMED: 'Confirmed',
  CHECKED_OUT: 'Checked Out',
  CANCELLED: 'Cancelled',
  WITH_DOCTOR: 'With Doctor',
  IN_ROOM: 'In Room',
  CHECKED_IN: 'Checked In',
  IN_ROOM_VITALS_TAKEN: 'In Room - Vitals Taken',
  NOT_SEEN: 'Not Seen',
  BILLED: 'Billed',
} as const
export type AppointmentStatus =
  (typeof AppointmentStatus)[keyof typeof AppointmentStatus]

// The 'statusstatus' is an unfortunate result of Elation's API design having a field called status inside of the status object.
const appointmentStatusStatusSchema = z.nativeEnum(AppointmentStatus)

export const toFhirAppointmentStatus: Record<
  AppointmentStatus,
  FhirAppointment['status']
> = {
  [AppointmentStatus.SCHEDULED]: 'booked',
  [AppointmentStatus.CONFIRMED]: 'booked',
  [AppointmentStatus.CHECKED_OUT]: 'fulfilled',
  [AppointmentStatus.CANCELLED]: 'cancelled',
  [AppointmentStatus.WITH_DOCTOR]: 'arrived',
  [AppointmentStatus.IN_ROOM]: 'arrived',
  [AppointmentStatus.CHECKED_IN]: 'checked-in',
  [AppointmentStatus.IN_ROOM_VITALS_TAKEN]: 'arrived',
  [AppointmentStatus.NOT_SEEN]: 'noshow',
  [AppointmentStatus.BILLED]: 'fulfilled',
}
export const fromFhirAppointmentStatus = invert(
  toFhirAppointmentStatus
) as Record<FhirAppointment['status'], AppointmentStatus>

const StatusDetail = {
  NO_SHOW: 'No Show',
  LEFT_WITHOUT_SEEING: 'Left Without Seeing',
  OTHER: 'Other',
} as const

export const appointmentStatusSchema = z
  .object({
    status: appointmentStatusStatusSchema.nullable().optional(),
    room: z.string().nullable().optional(), // Assuming you validate the value against available rooms elsewhere
    status_date: z.string().nullable().optional(), // ISO8601, read-only so optional on input
    status_detail: z
      .union([z.nativeEnum(StatusDetail), z.literal(null)])
      .nullable()
      .optional(),
  })
  .nullable()
  .optional() // Optional in "Create"

export const toFhirAppointmentParticipantStatusMap: Record<
  AppointmentStatus,
  AppointmentParticipant['status']
> = {
  [AppointmentStatus.SCHEDULED]: 'tentative',
  [AppointmentStatus.CONFIRMED]: 'accepted',
  [AppointmentStatus.CHECKED_OUT]: 'accepted',
  [AppointmentStatus.WITH_DOCTOR]: 'accepted',
  [AppointmentStatus.IN_ROOM]: 'accepted',
  [AppointmentStatus.CHECKED_IN]: 'accepted',
  [AppointmentStatus.IN_ROOM_VITALS_TAKEN]: 'accepted',
  [AppointmentStatus.BILLED]: 'accepted',
  [AppointmentStatus.CANCELLED]: 'declined',
  [AppointmentStatus.NOT_SEEN]: 'declined',
}
export const fromFhirAppointmentParticipantStatusMap = invert(
  toFhirAppointmentParticipantStatusMap
) as Record<AppointmentParticipant['status'], AppointmentStatus>

const serviceLocationSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  place_of_service: z.number().int().nullable().optional(),
  address_line1: z.string(),
  address_line2: z.string().nullable().optional(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  phone: z.string().nullable().optional(),
})

const billingDetailsSchema = z.object({
  billing_note: z.string().max(500),
  referring_provider: z.string().max(100),
  referring_provider_state: z.string().length(2),
})

const paymentDetailsSchema = z.object({
  id: z.number().int().nullable().optional(), // read-only
  amount: z.string(),
  when_collected: z.string(), // ISO8601
  bill: z.unknown().nullable().optional(), // Assuming 'null' for simplicity
  appointment: z.number().int(),
  create_date: z.string(), // ISO8601
  delete_date: z.string().nullable().optional(), // ISO8601
})

const APPOINTMENT_DESCRIPTION_MAX_LENGTH = 500
const APPOINTMENT_REASON_MAX_LENGTH = 50
const APPOINTMENT_INSTRUCTIONS_MAX_LENGTH = 500
export const appointmentSchema = z.object({
  id: z.number().int().nullable().optional(), // read-only
  scheduled_date: z.string(), // ISO8601
  duration: z
    .number()
    .int()
    .gte(1)
    .lte(1440)
    .refine((val) => val % 5 === 0, {
      message: 'Duration must be in 5-minute increments',
    }),
  time_slot_type: z.literal('appointment'),
  time_slot_status: z.unknown().nullable().optional(), // Assuming 'null' for simplicity
  reason: z.string().max(APPOINTMENT_REASON_MAX_LENGTH), // Values are mapped to "appointment types" https://arc.net/l/quote/pgdgahms
  description: z.string().max(APPOINTMENT_DESCRIPTION_MAX_LENGTH),
  status: appointmentStatusSchema,
  service_location: serviceLocationSchema.nullable().optional(),
  telehealth_details: z.string().nullable().optional(),
  patient: z.number().int(),
  physician: z.number().int(),
  practice: z.number().int(),
  recurring_event_schedule: z.unknown().nullable().optional(), // Assuming 'null' for simplicity
  billing_details: billingDetailsSchema.nullable().optional(), // Assuming optional for simplicity
  payment: paymentDetailsSchema.nullable().optional(),
  metadata: metadataSchema.nullable().optional(),
  created_date: z.string().nullable().optional(), // ISO8601, read-only
  last_modified_date: z.string().nullable().optional(), // ISO8601, read-only
  deleted_date: z.string().nullable().optional(), // ISO8601, read-only
  mode: z.union([z.literal('IN_PERSON'), z.literal('VIDEO')]),
  instructions: z.string().max(APPOINTMENT_INSTRUCTIONS_MAX_LENGTH),
})

export type Appointment = z.infer<typeof appointmentSchema>

export const fromFhir = ({
  appointment,
  patient,
  practitioner,
  healthcareService,
  practiceId,
}: {
  appointment: FhirAppointment
  patient: Patient
  practitioner: Practitioner
  healthcareService: HealthcareService
  practiceId: number
}): Appointment => {
  const elationAppointmentId = extractElationId(appointment)
  const elationPatientId = extractElationId(patient)
  const elationPractitionerId = extractElationId(practitioner)

  invariant(elationPatientId, 'Patient ID is required')
  invariant(elationPractitionerId, 'Practitioner ID is required')

  const startDate = new Date(appointment.start ?? '')
  const endDate = new Date(appointment.end ?? '')
  const duration = Math.round(
    (endDate.getTime() - startDate.getTime()) / (1000 * 60)
  )

  // https://arc.net/l/quote/yuksgoym
  // We should require that category and type are present on the HealthcareService resource.
  let reason = 'UNKNOWN'
  if (healthcareService.category && healthcareService.type) {
    const categoryCode = codeFromCodeableConcept(healthcareService.category[0])
    const typeCode = codeFromCodeableConcept(healthcareService.type[0])
    reason = `${categoryCode}-${typeCode}`
  }
  reason = reason.substring(0, APPOINTMENT_REASON_MAX_LENGTH)

  const result: Appointment = {
    id: elationAppointmentId,
    scheduled_date: startDate.toISOString(),
    duration,
    time_slot_type: 'appointment',
    reason,
    description: appointment.comment ?? '',
    status: {
      status: fromFhirAppointmentStatus[appointment.status],
      status_date: appointment.start,
    },
    patient: elationPatientId,
    physician: elationPractitionerId,
    practice: practiceId,
    mode:
      codeFromCodeableConcept(appointment.appointmentType) === 'VIRTUAL'
        ? 'VIDEO'
        : 'IN_PERSON',
    instructions: appointment.patientInstruction ?? '',
    created_date: appointment.created,
    last_modified_date: appointment.meta?.lastUpdated,
  }

  return addFhirRef(result, appointment)
}

export const toFhir = (
  appointment: Appointment,
  patient: Patient,
  practitioner?: Practitioner,
  practitionerRole?: PractitionerRole
): FhirAppointment => {
  const startDate: Date = new Date(appointment.scheduled_date)
  const endDate: Date = new Date(startDate)
  endDate.setMinutes(appointment.duration)

  const elationStatus = appointmentStatusStatusSchema.parse(
    appointment.status?.status ?? ''
  )
  const participantStatus = toFhirAppointmentParticipantStatusMap[elationStatus]

  const participantPatient: AppointmentParticipant = {
    actor: createReference(patient),
    status: participantStatus,
  }
  const participantPractitioner: AppointmentParticipant | undefined =
    practitioner
      ? {
          actor: createReference(practitioner),
          status: participantStatus,
        }
      : undefined
  const participant = compact([participantPatient, participantPractitioner])

  const status = toFhirAppointmentStatus[elationStatus]

  const specialty = getCodeableConceptBySystem(
    practitionerRole?.code,
    codeSystems.PRACTITIONER_ROLE
  )

  return build('Appointment', {
    identifier: [elationSandboxIdentifier(appointment.id?.toString())],
    start: startDate.toISOString(),
    end: endDate.toISOString(),
    status,
    participant,
    specialty: specialty ? [specialty] : undefined,
    created: appointment.created_date ?? undefined,
    description: appointment.reason,
    comment: appointment.description,
    meta: {
      tag: [
        {
          system: codeSystems.APPOINTMENT_ORIGIN,
          code: codeSystems.AppointmentOrigin.Seen,
        },
      ],
    },
    extension: [
      {
        url: codeSystems.EXTERNAL_RESOURCE_JSON_SYSTEM,
        valueString: JSON.stringify(appointment),
      },
    ],
  })
}
